//* Places View Toggle Controller
//*
//* Switches the places index between the card list and the full-screen map
//* view, the same way the member listings do: the map is a fixed full-viewport
//* overlay, so switching only toggles the overlay (plus a scroll lock) and the
//* list stays in place under it. The active view is mirrored into the URL
//* (?view=map) with replaceState, so it survives reloads and can be shared;
//* the server renders the initial state from the param.
//*
//* The kind filter is plain links, so filtering in list view is an ordinary page
//* visit — the links only carry the current view along. In map view the same
//* links apply in place: a visit there would tear the map down and rebuild it,
//* so the outlines and the count badge refresh from the index's json response
//* instead.
import ApplicationController from "application_controller.js";

export default class extends ApplicationController {
  debug = false;

  static targets = ["map", "listButton", "mapButton", "filterLink", "count"];

  connect() {
    //* A server-rendered ?view=map page (or a Turbo-restored one) must start
    //* with the scroll lock and button states in place
    this.syncPage(this.mapActive);
  }

  disconnect() {
    this.unlockScroll();
  }

  get mapActive() {
    return this.hasMapTarget && !this.mapTarget.classList.contains("d-none");
  }

  showMap() {
    if (!this.hasMapTarget) return;

    this.mapTarget.classList.remove("d-none");
    this.syncPage(true);
    this.updateUrl(true);
    //* Wakes the lazily-initialized places map (view_components/map, mode "places")
    window.dispatchEvent(new CustomEvent("places-map:activate"));
  }

  showList() {
    if (!this.hasMapTarget) return;

    //* Filtering in place only refreshed the map — the server-rendered card list
    //* under the overlay still holds the kind the page was loaded with, so
    //* returning to it needs a real visit
    if (this.filtersApplied) {
      const url = new URL(window.location);
      url.searchParams.delete("view");
      if (window.Turbo) window.Turbo.visit(url.toString());
      else window.location.assign(url.toString());
      return;
    }

    this.mapTarget.classList.add("d-none");
    this.syncPage(false);
    this.updateUrl(false);
  }

  //* Map view filters without a page (and so map) reload: refresh the outlines
  //* and the badge from the index's json response and mirror the link's params
  //* into the URL. List view keeps the plain navigation.
  filter(event) {
    if (!this.mapActive) return;

    event.preventDefault();
    this.applyFilterInPlace(event.currentTarget.href);
  }

  async applyFilterInPlace(href) {
    const url = new URL(href, window.location.origin);
    history.replaceState(history.state, "", url);
    this.filtersApplied = true;

    //* Both copies of the button group — the list header's and the map's — are
    //* targets, so the chosen kind lights up in whichever the visitor returns to
    const kind = url.searchParams.get("kind");
    this.filterLinkTargets.forEach((link) => {
      const linkKind = new URL(link.href, window.location.origin).searchParams.get("kind");
      link.classList.toggle("active", linkKind === kind);
    });

    const jsonParams = new URLSearchParams(url.searchParams);
    ["view", "page"].forEach((key) => jsonParams.delete(key));
    const jsonQuery = jsonParams.toString();
    const jsonUrl = `${url.pathname}.json${jsonQuery ? `?${jsonQuery}` : ""}`;

    let geojson;
    try {
      const response = await fetch(jsonUrl, { headers: { "Accept": "application/json" } });
      geojson = await response.json();
    } catch {
      return;
    }

    if (geojson.summary) this.countTargets.forEach((badge) => { badge.innerText = geojson.summary; });
    //* Handled by the places map (view_components/map, mode "places")
    window.dispatchEvent(new CustomEvent("places-map:update", { detail: { geojson, url: jsonUrl } }));
  }

  //* Mirrors the current view onto everything that carries view state: the
  //* toggle buttons, the scroll lock, and the filter links, which have to come
  //* back to the view the visitor was in
  syncPage(mapActive) {
    this.listButtonTargets.forEach((button) => button.classList.toggle("active", !mapActive));
    this.mapButtonTargets.forEach((button) => button.classList.toggle("active", mapActive));

    this.filterLinkTargets.forEach((link) => {
      const url = new URL(link.href, window.location.origin);
      if (mapActive) url.searchParams.set("view", "map");
      else url.searchParams.delete("view");
      link.href = url;
    });

    if (mapActive) this.lockScroll();
    else this.unlockScroll();
  }

  updateUrl(mapActive) {
    const url = new URL(window.location);
    if (mapActive) url.searchParams.set("view", "map");
    else url.searchParams.delete("view");
    history.replaceState(history.state, "", url);
  }

  lockScroll() {
    document.documentElement.classList.add("overflow-hidden");
    document.body.classList.add("overflow-hidden");
  }

  unlockScroll() {
    document.documentElement.classList.remove("overflow-hidden");
    document.body.classList.remove("overflow-hidden");
  }
}
